// ===== Dialogs: Order ticket, Modify, Indicators List, Properties, Symbols, Specification, About =====
import { useEffect, useRef, useState } from "react";
import { useStore, setUI, update, getState, openPosition, placeOrder, modifyPosition, closePosition, cancelOrder, chartOf, setChart, IND_DEFAULTS, ALERT_CONDS, CROSS_CONDS, TF_LIST, alertValue, saveAlert, fmtTime, beep } from "./store";
import { PriceDigits } from "./OneClick";
import { THEMES } from "./Chart";
import { I } from "./Icons";

const Win = ({ title, children, width = 420, testId }) => (
  <div className="dlg-overlay" onMouseDown={(e) => e.target === e.currentTarget && setUI({ dialog: null })}>
    <div className="dlg" style={{ width }} data-testid={testId}>
      <div className="dlg-title"><span>{title}</span><button className="dlg-x" data-testid="dialog-close" onClick={() => setUI({ dialog: null })}><I.close /></button></div>
      <div className="dlg-body">{children}</div>
    </div>
  </div>
);
const Row = ({ label, children }) => <div className="dlg-row"><label>{label}</label><div className="dlg-ctl">{children}</div></div>;

// Mini bid/ask tick chart shown on the left side of the order ticket
const TickChart = ({ symbol }) => {
  const ref = useRef(null);
  const st = useStore();
  useEffect(() => {
    const cv = ref.current, ctx = cv.getContext("2d");
    const C = THEMES[st.theme];
    const ticks = st.symbols[symbol].ticks;
    const W = cv.width, H = cv.height;
    ctx.fillStyle = C.bg; ctx.fillRect(0, 0, W, H);
    if (ticks.length < 2) return;
    let lo = Infinity, hi = -Infinity;
    ticks.forEach((t) => { lo = Math.min(lo, t.bid); hi = Math.max(hi, t.ask); });
    const pad = (hi - lo) * 0.1 || 1e-5;
    const y = (p) => 6 + ((hi + pad - p) / (hi - lo + 2 * pad)) * (H - 12);
    const x = (i) => (i / (ticks.length - 1)) * (W - 50);
    ctx.strokeStyle = C.grid; ctx.setLineDash([1, 2]);
    for (let i = 1; i < 5; i++) { ctx.beginPath(); ctx.moveTo(0, (H / 5) * i); ctx.lineTo(W - 50, (H / 5) * i); ctx.stroke(); }
    ctx.setLineDash([]);
    [["ask", "#E53935"], ["bid", "#1E88E5"]].forEach(([k, col]) => {
      ctx.strokeStyle = col; ctx.beginPath();
      ticks.forEach((t, i) => (i ? ctx.lineTo(x(i), y(t[k])) : ctx.moveTo(x(i), y(t[k]))));
      ctx.stroke();
    });
    const d = st.symbols[symbol].digits, last = ticks[ticks.length - 1];
    ctx.font = "10px Tahoma"; ctx.fillStyle = C.axis;
    ctx.fillText(last.ask.toFixed(d), W - 46, y(last.ask) - 2);
    ctx.fillText(last.bid.toFixed(d), W - 46, y(last.bid) + 10);
  });
  return <canvas ref={ref} width={260} height={290} className="tick-cv" data-testid="order-tick-chart" />;
};

const OrderDialog = ({ d }) => {
  const st = useStore();
  const [symbol, setSymbol] = useState(d.symbol);
  const [mode, setMode] = useState(d.mode || "market");
  const [ptype, setPtype] = useState(d.pendingType || "buy_limit");
  const [price, setPrice] = useState(d.price ? String(d.price) : st.symbols[d.symbol].bid.toFixed(st.symbols[d.symbol].digits));
  const [vol, setVol] = useState(d.volume ? (+d.volume).toFixed(2) : "0.10");
  const [sl, setSl] = useState("0.00");
  const [tp, setTp] = useState("0.00");
  const [comment, setComment] = useState("");
  const [err, setErr] = useState("");
  const s = st.symbols[symbol];
  const valid = () => { if (!(+vol > 0) || +vol > 100) { setErr("Invalid volume"); return false; } return true; };
  const market = (type) => {
    if (!valid()) return;
    const px = type === "buy" ? s.ask : s.bid;
    if (+sl && (type === "buy" ? +sl >= px : +sl <= px)) return setErr("Invalid S/L");
    if (+tp && (type === "buy" ? +tp <= px : +tp >= px)) return setErr("Invalid T/P");
    openPosition(symbol, type, +vol, sl, tp, comment);
    setUI({ dialog: null });
  };
  const place = () => {
    if (!valid() || !(+price > 0)) return setErr("Invalid price");
    placeOrder(symbol, ptype, +vol, +price, sl, tp, comment);
    setUI({ dialog: null });
  };
  return (
    <Win title={`Order: ${symbol}, ${s.desc}`} width={640} testId="order-dialog">
      <div className="order-wrap">
        <div className="order-left"><div className="tick-title">{symbol}, Tick chart</div><TickChart symbol={symbol} /></div>
        <div className="order-right">
          <Row label="Symbol:">
            <select data-testid="order-symbol" value={symbol} onChange={(e) => { setSymbol(e.target.value); setPrice(st.symbols[e.target.value].bid.toFixed(st.symbols[e.target.value].digits)); }}>
              {Object.values(st.symbols).map((x) => <option key={x.name} value={x.name}>{x.name}, {x.desc}</option>)}
            </select>
          </Row>
          <Row label="Type:">
            <select data-testid="order-type" value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="market">Market Execution</option><option value="pending">Pending Order</option>
            </select>
          </Row>
          <Row label="Volume:"><input data-testid="order-volume" value={vol} onChange={(e) => setVol(e.target.value)} /><span className="dlg-hint">{(+vol * s.contract).toLocaleString()} units</span></Row>
          {mode === "pending" && (
            <>
              <Row label="Type:">
                <select data-testid="order-pending-type" value={ptype} onChange={(e) => setPtype(e.target.value)}>
                  <option value="buy_limit">Buy Limit</option><option value="sell_limit">Sell Limit</option><option value="buy_stop">Buy Stop</option><option value="sell_stop">Sell Stop</option>
                </select>
              </Row>
              <Row label="Price:"><input data-testid="order-price" value={price} onChange={(e) => setPrice(e.target.value)} /></Row>
            </>
          )}
          <Row label="Stop Loss:"><input data-testid="order-sl" value={sl} onChange={(e) => setSl(e.target.value)} /></Row>
          <Row label="Take Profit:"><input data-testid="order-tp" value={tp} onChange={(e) => setTp(e.target.value)} /></Row>
          <Row label="Comment:"><input data-testid="order-comment" value={comment} onChange={(e) => setComment(e.target.value)} /></Row>
          {mode === "market" ? (
            <>
              <div className="order-quote" data-testid="order-quote">
                <span className="q-bid"><PriceDigits price={s.bid} digits={s.digits} /></span><span className="q-sep">/</span><span className="q-ask"><PriceDigits price={s.ask} digits={s.digits} /></span>
              </div>
              <div className="order-btns">
                <button className="btn-sell" data-testid="order-sell-btn" onClick={() => market("sell")}>Sell by Market</button>
                <button className="btn-buy" data-testid="order-buy-btn" onClick={() => market("buy")}>Buy by Market</button>
              </div>
            </>
          ) : (
            <div className="order-btns"><button className="btn-place" data-testid="order-place-btn" onClick={place}>Place</button></div>
          )}
          {err && <div className="dlg-err" data-testid="order-error">{err}</div>}
          <div className="dlg-note">Attention! The trade will be executed at market conditions, difference with requested price may be significant!</div>
        </div>
      </div>
    </Win>
  );
};

const ModifyDialog = ({ d }) => {
  const st = getState();
  const p = st.positions.find((x) => x.ticket === d.ticket) || st.orders.find((x) => x.ticket === d.ticket);
  const [sl, setSl] = useState(p ? String(p.sl || "0.00") : "");
  const [tp, setTp] = useState(p ? String(p.tp || "0.00") : "");
  if (!p) return null;
  const isPos = st.positions.includes(p);
  return (
    <Win title={`Position #${p.ticket} ${p.type} ${p.volume.toFixed(2)} ${p.symbol}`} width={360} testId="modify-dialog">
      <Row label="Stop Loss:"><input data-testid="modify-sl" value={sl} onChange={(e) => setSl(e.target.value)} /></Row>
      <Row label="Take Profit:"><input data-testid="modify-tp" value={tp} onChange={(e) => setTp(e.target.value)} /></Row>
      <div className="order-btns">
        <button className="btn-place" data-testid="modify-save" onClick={() => { modifyPosition(p.ticket, sl, tp); setUI({ dialog: null }); }}>Modify</button>
        <button className="btn-sell" data-testid="modify-close" onClick={() => { isPos ? closePosition(p.ticket) : cancelOrder(p.ticket); setUI({ dialog: null }); }}>{isPos ? "Close" : "Delete"}</button>
      </div>
    </Win>
  );
};

const IndicatorsDialog = ({ d }) => {
  const st = useStore();
  const c = chartOf(d.chartId);
  const edit = (id, k, v) => update(() => { const i = c.indicators.find((x) => x.id === id); i[k] = Math.max(1, +v || 1); });
  return (
    <Win title={`Indicators on ${c.symbol},${c.tf}`} width={460} testId="indicators-dialog">
      <table className="mt-table dlg-table">
        <thead><tr><th className="l">Indicator</th><th className="l">Parameters</th><th className="l">Color</th><th></th></tr></thead>
        <tbody>
          {c.indicators.map((i) => (
            <tr key={i.id} data-testid={`ind-row-${i.type}`}>
              <td className="l">{i.type}</td>
              <td className="l">{Object.keys(IND_DEFAULTS[i.type]).filter((k) => k !== "color").map((k) => <span key={k} className="ind-param">{k}<input value={i[k]} onChange={(e) => edit(i.id, k, e.target.value)} /></span>)}</td>
              <td className="l"><input type="color" value={i.color} onChange={(e) => update(() => { i.color = e.target.value; })} /></td>
              <td><button className="btn-small" data-testid={`ind-delete-${i.type}`} onClick={() => setChart({ indicators: c.indicators.filter((x) => x.id !== i.id) }, c.id)}>Delete</button></td>
            </tr>
          ))}
          {!c.indicators.length && <tr><td className="l sec" colSpan={4}>No indicators. Use Insert → Indicators or drag from Navigator.</td></tr>}
        </tbody>
      </table>
      <div className="order-btns"><button className="btn-place" onClick={() => setUI({ dialog: null })}>Close</button></div>
      <span style={{ display: "none" }}>{st.version}</span>
    </Win>
  );
};

const PropertiesDialog = ({ d }) => {
  const st = useStore();
  const c = chartOf(d.chartId);
  const chk = (k, l) => <label className="dlg-chk"><input type="checkbox" data-testid={`prop-${k}`} checked={!!c[k]} onChange={() => setChart({ [k]: !c[k] }, c.id)} /> {l}</label>;
  return (
    <Win title={`${c.symbol},${c.tf}`} width={380} testId="properties-dialog">
      <Row label="Color scheme:">
        <select data-testid="prop-theme" value={st.theme} onChange={(e) => update((s) => { s.theme = e.target.value; })}><option value="dark">Dark (Black on White inverted)</option><option value="light">Light (Black on White)</option></select>
      </Row>
      <Row label="Chart type:">
        <select data-testid="prop-type" value={c.type} onChange={(e) => setChart({ type: e.target.value }, c.id)}><option value="bars">Bar Chart</option><option value="candles">Candlesticks</option><option value="line">Line Chart</option></select>
      </Row>
      <div className="dlg-checks">{chk("grid", "Show grid")}{chk("volumes", "Show volumes")}{chk("autoScroll", "Auto scroll")}{chk("oneClick", "Show One Click Trading")}</div>
      <div className="order-btns"><button className="btn-place" onClick={() => setUI({ dialog: null })}>OK</button></div>
    </Win>
  );
};

const SymbolsDialog = () => {
  const st = useStore();
  return (
    <Win title="Symbols" width={460} testId="symbols-dialog">
      <div className="mt-scroll" style={{ maxHeight: 320 }}>
        <table className="mt-table dlg-table">
          <thead><tr><th className="l">Symbol</th><th className="l">Description</th><th>Show</th></tr></thead>
          <tbody>
            {Object.values(st.symbols).map((s) => (
              <tr key={s.name}><td className="l">{s.name}</td><td className="l">{s.desc}</td>
                <td><input type="checkbox" data-testid={`sym-show-${s.name}`} checked={st.watch.includes(s.name)} onChange={() => update((x) => { x.watch = x.watch.includes(s.name) ? x.watch.filter((n) => n !== s.name) : [...x.watch, s.name]; })} /></td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </Win>
  );
};

const SpecDialog = ({ d }) => {
  const s = getState().symbols[d.symbol];
  const rows = [["Description", s.desc], ["Digits", s.digits], ["Contract size", s.contract], ["Spread", "floating"], ["Stops level", 0], ["Margin currency", d.symbol.slice(0, 3)], ["Profit currency", d.symbol.length === 6 ? d.symbol.slice(3) : "USD"], ["Minimal volume", "0.01"], ["Maximal volume", "100.00"], ["Volume step", "0.01"], ["Trade", "Full access"], ["Execution", "Market"]];
  return <Win title={`${d.symbol}, specification`} width={360} testId="spec-dialog"><table className="mt-table dlg-table"><tbody>{rows.map((r) => <tr key={r[0]}><td className="l">{r[0]}</td><td className="l">{r[1]}</td></tr>)}</tbody></table></Win>;
};

const AboutDialog = () => (
  <Win title="About" width={360} testId="about-dialog">
    <div className="about"><I.newChart /><div><b>Web Trading Terminal</b><br />Version 5.00 build 4410<br />Simulated demo environment — prices are random-walk generated.<br /><br />Account 51842907 · WebTrade-Demo · 1:100</div></div>
    <div className="order-btns"><button className="btn-place" onClick={() => setUI({ dialog: null })}>OK</button></div>
  </Win>
);

const SignalsDialog = () => (
  <Win title="Signals" width={420} testId="signals-dialog">
    <table className="mt-table dlg-table"><thead><tr><th className="l">Signal</th><th>Growth</th><th>Subscribers</th></tr></thead>
      <tbody>{[["Steady Scalper", "+184%", 412], ["Gold Trend Pro", "+96%", 238], ["FX Carry Basket", "+41%", 155]].map((r) => <tr key={r[0]}><td className="l">{r[0]}</td><td className="pos">{r[1]}</td><td>{r[2]}</td></tr>)}</tbody></table>
  </Win>
);

// Drawn object properties: color, width, style, anchor values
const ObjectDialog = ({ d }) => {
  const st = useStore();
  const c = chartOf(d.chartId);
  const o = c.drawings.find((x) => x.id === d.id);
  if (!o) return null;
  const dg = st.symbols[c.symbol].digits;
  const set = (k, val) => update(() => { o[k] = val; });
  const names = { hline: "Horizontal Line", vline: "Vertical Line", trend: "Trendline", rect: "Rectangle", fib: "Fibonacci Retracement" };
  return (
    <Win title={`${names[o.type]} — properties`} width={360} testId="object-dialog">
      <Row label="Color:"><input type="color" data-testid="object-color" value={o.color || "#FFB300"} onChange={(e) => set("color", e.target.value)} /></Row>
      <Row label="Width:">
        <select data-testid="object-width" value={o.width || 1} onChange={(e) => set("width", +e.target.value)}>{[1, 2, 3, 4].map((w) => <option key={w} value={w}>{w}px</option>)}</select>
      </Row>
      <Row label="Style:">
        <select data-testid="object-style" value={o.style || "solid"} onChange={(e) => set("style", e.target.value)}><option value="solid">Solid</option><option value="dash">Dash</option><option value="dot">Dot</option></select>
      </Row>
      {o.type !== "vline" && <Row label="Price 1:"><input data-testid="object-p1" defaultValue={o.p1.toFixed(dg)} onBlur={(e) => +e.target.value && set("p1", +e.target.value)} /></Row>}
      {o.p2 != null && <Row label="Price 2:"><input data-testid="object-p2" defaultValue={o.p2.toFixed(dg)} onBlur={(e) => +e.target.value && set("p2", +e.target.value)} /></Row>}
      <Row label="Time 1:"><span className="dlg-hint">{fmtTime(o.t1)}</span></Row>
      <div className="order-btns">
        <button className="btn-place" data-testid="object-ok" onClick={() => setUI({ dialog: null })}>OK</button>
        <button className="btn-sell" data-testid="object-delete" onClick={() => { setChart({ drawings: c.drawings.filter((x) => x.id !== o.id) }, c.id); setUI({ dialog: null }); }}>Delete</button>
      </div>
    </Win>
  );
};

// Create / modify an alert: price level, RSI threshold cross, or moving-average cross
const AlertDialog = ({ d }) => {
  const st = useStore();
  const ex = d.id ? st.alerts.find((a) => a.id === d.id) : null;
  const sym0 = d.symbol || "EURUSD";
  const [f, setF] = useState(ex ? { kind: "price", tf: "H1", period: 14, level: 70, maType: "SMA", fast: 10, slow: 50, cross: "crosses", cond: "Bid >", value: st.symbols[ex.symbol].bid, ...ex } : {
    kind: d.alertKind || "price", symbol: sym0, cond: d.cond || "Bid >", value: d.value ?? st.symbols[sym0].bid,
    tf: d.tf || "H1", period: 14, level: 70, maType: "SMA", fast: 10, slow: 50, cross: "crosses",
    action: "Sound", limit: 1, comment: "", enabled: true,
  });
  const [err, setErr] = useState("");
  const s = st.symbols[f.symbol];
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const validInd = f.kind === "rsi" ? +f.period >= 2 && +f.level > 0 && +f.level < 100 : +f.fast >= 1 && +f.slow > +f.fast;
  const cur = f.kind !== "price" && validInd ? alertValue(f) : null;
  const ok = () => {
    if (f.kind === "price" && !(+f.value > 0)) return setErr("Invalid value");
    if (f.kind === "rsi" && !validInd) return setErr("RSI period must be ≥ 2 and level between 0 and 100");
    if (f.kind === "ma" && !validInd) return setErr("Slow period must be greater than fast period");
    saveAlert({ ...f, counter: 0 });
    setUI({ dialog: null });
  };
  return (
    <Win title={ex ? "Modify Alert" : "Create Alert"} width={380} testId="alert-dialog">
      <label className="dlg-chk" style={{ marginBottom: 8 }}><input type="checkbox" data-testid="alert-enabled" checked={f.enabled} onChange={set("enabled")} /> Enable</label>
      <Row label="Alert type:">
        <select data-testid="alert-kind" value={f.kind} onChange={set("kind")}><option value="price">Price level</option><option value="rsi">RSI threshold cross</option><option value="ma">Moving average cross</option></select>
      </Row>
      <Row label="Action:"><select data-testid="alert-action" value={f.action} onChange={set("action")}><option>Sound</option><option>Notification</option></select><button className="btn-small" onClick={beep}>▶</button></Row>
      <Row label="Symbol:"><select data-testid="alert-symbol" value={f.symbol} onChange={(e) => setF({ ...f, symbol: e.target.value, value: st.symbols[e.target.value].bid })}>{Object.keys(st.symbols).map((n) => <option key={n}>{n}</option>)}</select></Row>
      {f.kind === "price" ? (
        <>
          <Row label="Condition:"><select data-testid="alert-cond" value={f.cond} onChange={set("cond")}>{ALERT_CONDS.map((c) => <option key={c}>{c}</option>)}</select></Row>
          <Row label="Value:"><input data-testid="alert-value" value={f.value} onChange={set("value")} /><span className="dlg-hint">bid {s.bid.toFixed(s.digits)}</span></Row>
        </>
      ) : (
        <>
          <Row label="Timeframe:"><select data-testid="alert-tf" value={f.tf} onChange={set("tf")}>{TF_LIST.map((t) => <option key={t}>{t}</option>)}</select></Row>
          {f.kind === "rsi" ? (
            <>
              <Row label="RSI period:"><input data-testid="alert-rsi-period" value={f.period} onChange={set("period")} /></Row>
              <Row label="Condition:"><select data-testid="alert-cross" value={f.cross} onChange={set("cross")}>{CROSS_CONDS.map((c) => <option key={c}>{c}</option>)}</select></Row>
              <Row label="Level:"><input data-testid="alert-rsi-level" value={f.level} onChange={set("level")} /><button className="btn-small" onClick={() => setF({ ...f, level: 70 })}>70</button><button className="btn-small" onClick={() => setF({ ...f, level: 30 })}>30</button></Row>
            </>
          ) : (
            <>
              <Row label="MA method:"><select data-testid="alert-ma-type" value={f.maType} onChange={set("maType")}><option value="SMA">Simple (SMA)</option><option value="EMA">Exponential (EMA)</option></select></Row>
              <Row label="Fast period:"><input data-testid="alert-ma-fast" value={f.fast} onChange={set("fast")} /></Row>
              <Row label="Slow period:"><input data-testid="alert-ma-slow" value={f.slow} onChange={set("slow")} /></Row>
              <Row label="Condition:"><select data-testid="alert-cross" value={f.cross} onChange={set("cross")}>{CROSS_CONDS.map((c) => <option key={c} value={c}>fast {c} slow</option>)}</select></Row>
            </>
          )}
          <div className="dlg-cur" data-testid="alert-current">
            {cur && cur.v != null && cur.ref != null
              ? f.kind === "rsi"
                ? <>Current RSI({f.period}): <b>{cur.v.toFixed(2)}</b> — {cur.v > cur.ref ? "above" : "below"} {f.level}</>
                : <>Current fast: <b>{cur.v.toFixed(s.digits)}</b> slow: <b>{cur.ref.toFixed(s.digits)}</b> — fast is {cur.v > cur.ref ? "above" : "below"}</>
              : "Not enough data / invalid parameters"}
          </div>
          <div className="dlg-note">Fires when the value crosses on the live (forming) bar.</div>
        </>
      )}
      <Row label="Max iterations:"><input data-testid="alert-limit" value={f.limit} onChange={set("limit")} /></Row>
      <Row label="Comment:"><input data-testid="alert-comment" value={f.comment} onChange={set("comment")} /></Row>
      {err && <div className="dlg-err" data-testid="alert-error">{err}</div>}
      <div className="order-btns"><button className="btn-place" data-testid="alert-ok" onClick={ok}>OK</button><button className="btn-place" onClick={() => setUI({ dialog: null })}>Cancel</button></div>
    </Win>
  );
};

// Fired alerts popup (bottom-right)
export const AlertPopup = () => {
  const { popups } = useStore();
  if (!popups.length) return null;
  return (
    <div className="alert-pop" data-testid="alert-popup">
      <div className="dlg-title"><span>Alert</span><button className="dlg-x" data-testid="alert-popup-close" onClick={() => setUI({ popups: [] })}><I.close /></button></div>
      <div className="alert-pop-body">
        {popups.slice(0, 8).map((p) => <div key={p.id} className="alert-pop-row"><span className="sec">{fmtTime(p.time, true)}</span> {p.msg}</div>)}
      </div>
    </div>
  );
};

export default function Dialogs() {
  const { dialog: d } = useStore();
  if (!d) return null;
  const M = { order: OrderDialog, modify: ModifyDialog, indicators: IndicatorsDialog, properties: PropertiesDialog, symbols: SymbolsDialog, spec: SpecDialog, about: AboutDialog, signals: SignalsDialog, object: ObjectDialog, alert: AlertDialog }[d.kind];
  return M ? <M d={d} key={JSON.stringify(d)} /> : null;
}
